import type { FunctionsResponse } from "@supabase/functions-js";

export interface User {
  id: string;
  email?: string;
  user_metadata?: Record<string, any>;
  is_anonymous?: boolean;
}

export interface TotpFactor {
  id: string;
  friendly_name?: string | null;
  status: "verified" | "unverified";
}

export interface Session {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  expires_at?: number;
  token_type: string;
  user: User;
  adapter: string;
}

export type ResponseWithError<T> =
  | {
      error: null | undefined;
      data: T;
    }
  | {
      error: Error;
      data: null | undefined;
    };

export enum AuthEvent {
  INITIAL_SESSION = "INITIAL_SESSION",
  PASSWORD_RECOVERY = "PASSWORD_RECOVERY",
  SIGNED_IN = "SIGNED_IN",
  SIGNED_OUT = "SIGNED_OUT",
  TOKEN_REFRESHED = "TOKEN_REFRESHED",
  USER_UPDATED = "USER_UPDATED",
}

const AuthEvents = Object.values(AuthEvent);
export function isAuthEvent(event: string): event is AuthEvent {
  return AuthEvents.includes(event as AuthEvent);
}

type CallbackFn = (event: AuthEvent, session: Session | null) => void;

export abstract class SessionAdapter {
  get canSignUp() {
    return false;
  }
  get canSignIn() {
    return false;
  }
  get canSignOut() {
    return false;
  }
  get canResetPassword() {
    return false;
  }

  signOut(): Promise<ResponseWithError<undefined>> {
    throw new Error("signOut not implemented");
  }

  signUp(
    _email: string,
    _password: string,
    _options?: {
      data: {
        name: string;
        country?: string;
        timezone?: string;
        inviteToken?: string;
        registerClubData?: Record<string, any>;
      };
    },
  ): Promise<any> {
    throw new Error("signUp not implemented");
  }

  signInWithPassword(
    _email: string,
    _password: string,
  ): Promise<ResponseWithError<{ session: Session; user: User }>> {
    throw new Error("signInWithPassword not implemented");
  }

  resetPasswordForEmail(_email: string, _redirectTo: string): Promise<ResponseWithError<any>> {
    throw new Error("resetPasswordForEmail not implemented");
  }

  verifyRecoveryOtp(
    _email: string,
    _token: string,
  ): Promise<ResponseWithError<{ session: Session; user: User }>> {
    throw new Error("verifyRecoveryOtp not implemented");
  }

  updatePassword(_password: string): Promise<ResponseWithError<any>> {
    throw new Error("updatePassword not implemented");
  }

  updateName(_name: string): Promise<ResponseWithError<any>> {
    throw new Error("updateName not implemented");
  }

  updateEmail(_email: string): Promise<ResponseWithError<any>> {
    throw new Error("updateEmail not implemented");
  }

  getUser(): Promise<ResponseWithError<{ user: User }>> {
    throw new Error("getUser not implemented");
  }

  abstract getSession(): Promise<Session | null>;

  _listeners: CallbackFn[] = [];

  onAuthStateChange(callback: (event: AuthEvent, session: Session | null) => void) {
    this._listeners.push(callback);
    this.getSession().then((session) => {
      callback(AuthEvent.INITIAL_SESSION, session);
    });
  }

  abstract rpc<T>(
    functionName: string,
    args: any,
  ): Promise<
    | {
        error: undefined;
        data: T;
      }
    | { error: Error; data: undefined }
  >;

  abstract edgeFunction(functionName: string, args: any): Promise<FunctionsResponse<any>>;

  abstract initialize(): Promise<void>;

  listTotpFactors(): Promise<ResponseWithError<{ factors: TotpFactor[] }>> {
    throw new Error("listTotpFactors not implemented");
  }

  hasTotpFactor(): Promise<ResponseWithError<{ enrolled: boolean }>> {
    throw new Error("hasTotpFactor not implemented");
  }

  enrollTotp(
    _friendlyName?: string,
  ): Promise<ResponseWithError<{ factorId: string; qrCode: string; secret: string; uri: string }>> {
    throw new Error("enrollTotp not implemented");
  }

  verifyTotpEnrollment(
    _factorId: string,
    _code: string,
  ): Promise<ResponseWithError<{ accessToken?: string }>> {
    throw new Error("verifyTotpEnrollment not implemented");
  }

  challengeAndVerifyTotp(
    _factorId: string,
    _code: string,
  ): Promise<ResponseWithError<{ accessToken?: string }>> {
    throw new Error("challengeAndVerifyTotp not implemented");
  }

  /**
   * Apply an elevated access token after edge MFA step-up (Tauri device-token JWTs).
   * Web GoTrue sessions usually elevate via challengeAndVerifyTotp instead.
   */
  applyElevatedAccessToken(_accessToken: string): Promise<ResponseWithError<undefined>> {
    throw new Error("applyElevatedAccessToken not implemented");
  }

  unenrollTotp(_factorId: string): Promise<ResponseWithError<undefined>> {
    throw new Error("unenrollTotp not implemented");
  }

  protected emit(event: AuthEvent, session: Session | null) {
    this._listeners.forEach((listener) => {
      listener(event, session);
    });
  }
}
